import { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Container,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { ThemeSelector } from '../ThemeSelector';
import { getCurrentUser, logout } from '../auth/LoginButton';

// Helper function to determine if current theme is dark
const isDarkTheme = (themeName: string): boolean => {
  return themeName === 'midnight' || themeName === 'navy';
};

// Helper function to get the appropriate logo based on theme
const getLogoPath = (themeName: string): string => {
  return isDarkTheme(themeName) 
    ? '/images/Logo with blue as white.png'
    : '/images/Transparent full color logo.png';
};

interface ECMLayoutProps {
  children: ReactNode;
  currentTheme: string;
  onThemeChange: (theme: string) => void;
}

export default function ECMLayout({ children, currentTheme, onThemeChange }: ECMLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/SIMS');
  };

  const drawer = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      paddingTop: 6,
      paddingX: 3
    }}>
      {/* Top-aligned Logo and Branding */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: 2
      }}>
        <Box
          component="img"
          src={getLogoPath(currentTheme)}
          alt="Laurus College"
          sx={{
            width: 120,
            height: 120,
            objectFit: 'contain',
            filter: 'drop-shadow(0 4px 8px rgba(255, 215, 0, 0.2))',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />
        <Box sx={{ textAlign: 'center' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'var(--accent)', 
              fontWeight: 600,
              letterSpacing: '0.5px',
              mb: 0.5
            }}
          >
            Campaign Manager
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'var(--text-secondary)',
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.7rem'
            }}
          >
            SIMS ECM
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--bg-primary)',
      zIndex: 1000,
      overflow: 'auto', 
      '& .MuiAppBar-root': {
        backgroundColor: 'var(--bg-secondary)',
        color: 'var(--text-primary)'
      },
      '& .MuiDrawer-paper': {
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      },
      '& .MuiListItemButton-root': {
        color: 'var(--text-primary)',
        '&:hover': {
          backgroundColor: 'var(--bg-secondary)'
        },
        '&.Mui-selected': {
          backgroundColor: 'var(--accent)',
          color: 'var(--button-text)',
          '&:hover': {
            backgroundColor: 'var(--accent-hover)'
          }
        }
      },
      '& .MuiListItemIcon-root': {
        color: 'var(--text-primary)'
      },
      '& .MuiListItemText-primary': {
        color: 'var(--text-primary)'
      },
      '& .MuiTypography-root': {
        color: 'var(--text-primary)'
      },
      '& .MuiMenu-paper': {
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)'
      },
      '& .MuiMenuItem-root': {
        color: 'var(--text-primary)',
        '&:hover': {
          backgroundColor: 'var(--bg-secondary)'
        }
      }
    }}>
      <AppBar position="fixed" sx={{ zIndex: (theme: any) => theme.zIndex.drawer + 1, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', left: 0, right: 0 }}>
        <Toolbar sx={{ px: 0, minHeight: '56px !important', height: '56px' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <ThemeSelector currentTheme={currentTheme} onThemeChange={onThemeChange} />
            </Box>
            <Typography variant="body1" sx={{ color: 'var(--text-primary)' }}>
              {user?.name || user?.email || 'User'}
            </Typography>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="body2">{user?.email || user?.name || 'User'}</Typography>
        </MenuItem>
        {user?.role !== 'Campaign.Manager' && (
          <MenuItem onClick={() => { handleMenuClose(); navigate('/Surveys'); }}>Back to Surveys</MenuItem>
        )}
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { sm: 240 }, flexShrink: 0, margin: 0, padding: 0 }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 240,
              margin: 0,
              padding: 0,
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              borderColor: 'var(--card-border)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 240,
              margin: 0,
              padding: 0,
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              borderColor: 'var(--card-border)',
              borderRight: 0
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - 240px)` },
          minHeight: '100vh',
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          position: 'relative',
          zIndex: 1,
          marginTop: '56px'
        }}
      >
        {children}
        <footer style={{
          borderTop: '1px solid var(--card-border)',
          marginTop: '3rem',
          padding: '1.25rem 2rem',
          textAlign: 'center',
          background: 'var(--bg-secondary)',
        }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '2px' }}>
            Laurus College — Student Information Management System
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            © {new Date().getFullYear()} Laurus College. All rights reserved.
          </div>
        </footer>
      </Box>
    </Box>
  );
}
